/**
 * Circled ("bubble") text for the Bubble Text Generator.
 *
 * Letters: Circled Latin A-Z U+24B6-24CF and a-z U+24D0-24E9; filled capitals
 * U+1F150-1F169 (Unicode has no filled lowercase). Digits: CIRCLED DIGIT ONE-NINE
 * U+2460-2468, CIRCLED DIGIT ZERO U+24EA; DINGBAT NEGATIVE CIRCLED DIGIT ONE-NINE
 * U+2776-277E, NEGATIVE CIRCLED DIGIT ZERO U+24FF.
 *
 * Whole numbers are single characters where Unicode has them (checked against the
 * Unicode Character Database):
 *
 * - outlined 10-20: CIRCLED NUMBER TEN-TWENTY U+2469-2473
 * - outlined 21-35: CIRCLED NUMBER TWENTY ONE-THIRTY FIVE U+3251-325F
 * - outlined 36-50: CIRCLED NUMBER THIRTY SIX-FIFTY U+32B1-32BF
 * - filled 10: DINGBAT NEGATIVE CIRCLED NUMBER TEN U+277F
 * - filled 11-20: NEGATIVE CIRCLED NUMBER ELEVEN-TWENTY U+24EB-24F4
 *
 * There are no filled circles above 20. U+3251-32BF live in the CJK block, so some fonts
 * draw them full-width.
 */

export type BubbleStyle = "outlined" | "filled";

const CODE_A_LOWER = 97;
const CODE_A_UPPER = 65;
const CODE_ONE = 49;
const DIGIT_RUNS = /(\d+)/;
const TWO_DIGITS = /^[1-9]\d$/;

function buildOutlinedMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (let i = 0; i < 26; i++) {
    map.set(
      String.fromCharCode(CODE_A_LOWER + i),
      String.fromCodePoint(0x24_d0 + i)
    );
    map.set(
      String.fromCharCode(CODE_A_UPPER + i),
      String.fromCodePoint(0x24_b6 + i)
    );
  }
  for (let i = 0; i < 9; i++) {
    map.set(
      String.fromCharCode(CODE_ONE + i),
      String.fromCodePoint(0x24_60 + i)
    );
  }
  map.set("0", "⓪");
  return map;
}

function buildFilledMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (let i = 0; i < 26; i++) {
    const filled = String.fromCodePoint(0x1_f1_50 + i);
    map.set(String.fromCharCode(CODE_A_UPPER + i), filled);
    map.set(String.fromCharCode(CODE_A_LOWER + i), filled);
  }
  for (let i = 0; i < 9; i++) {
    map.set(
      String.fromCharCode(CODE_ONE + i),
      String.fromCodePoint(0x27_76 + i)
    );
  }
  map.set("0", "⓿");
  return map;
}

const OUTLINED_MAP = buildOutlinedMap();
const FILLED_MAP = buildFilledMap();

/** The single circled character for a whole number, if Unicode has one. */
export function circledNumber(
  value: number,
  style: BubbleStyle
): string | null {
  if (style === "filled") {
    if (value === 10) {
      return "❿";
    }
    if (value >= 11 && value <= 20) {
      return String.fromCodePoint(0x24_eb + (value - 11));
    }
    return null;
  }
  if (value >= 10 && value <= 20) {
    return String.fromCodePoint(0x24_69 + (value - 10));
  }
  if (value >= 21 && value <= 35) {
    return String.fromCodePoint(0x32_51 + (value - 21));
  }
  if (value >= 36 && value <= 50) {
    return String.fromCodePoint(0x32_b1 + (value - 36));
  }
  return null;
}

function mapChars(text: string, map: Map<string, string>): string {
  return [...text].map((ch) => map.get(ch) ?? ch).join("");
}

/**
 * Circles every letter and digit. A run of exactly two digits from 10 up (no leading
 * zero) becomes one circled number where Unicode has it, unless `keepDigits` is set;
 * longer runs ("2026", "100") stay digit by digit so a year never turns into two numbers.
 */
export function toBubble(
  text: string,
  style: BubbleStyle,
  { keepDigits = false }: { keepDigits?: boolean } = {}
): string {
  const map = style === "filled" ? FILLED_MAP : OUTLINED_MAP;
  return text
    .split(DIGIT_RUNS)
    .map((part, index) => {
      if (index % 2 === 1 && !keepDigits && TWO_DIGITS.test(part)) {
        return circledNumber(Number(part), style) ?? mapChars(part, map);
      }
      return mapChars(part, map);
    })
    .join("");
}
